import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Heading,
  Button,
  Card,
  CardBody,
  CardHeader,
  Text,
  VStack,
  HStack,
  FormControl,
  FormLabel,
  Input,
  Textarea,
  Switch,
  NumberInput,
  NumberInputField,
  NumberInputStepper,
  NumberIncrementStepper,
  NumberDecrementStepper,
  Select,
  Alert,
  AlertIcon,
  useToast,
  Skeleton,
} from '@chakra-ui/react';
import { FiSave, FiArrowLeft } from 'react-icons/fi';
import { useNavigate, useParams } from 'react-router-dom';
import { useVoiceService, VoiceGroup } from '../../hooks/useVoiceService';
import { useUserProfile } from '../../contexts/userprofile/UserProfileContext';

export const VoiceGroupSettingsPage: React.FC = () => {
  const { groupId } = useParams<{ groupId: string }>();
  const [group, setGroup] = useState<VoiceGroup | null>(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    type: 2,
    settings: {
      max_members: 20,
      is_public: true,
      allow_guest: false,
      require_approval: false,
      join_code: '',
      audio_quality: 2,
    },
  });
  
  const { profile } = useUserProfile();
  const navigate = useNavigate();
  const toast = useToast();
  const { getVoiceGroup } = useVoiceService();

  const loadGroup = useCallback(async () => {
    if (!groupId) return;
    
    try {
      setLoading(true);
      setError(null);
      
      const response = await getVoiceGroup(groupId);
      const groupData = response.voice_group;
      setGroup(groupData);
      
      // Populate form with group data
      setFormData({
        name: groupData.name || '',
        description: groupData.description || '',
        type: groupData.type || 2,
        settings: {
          max_members: groupData.settings?.max_members || 20,
          is_public: groupData.settings?.is_public || true,
          allow_guest: groupData.settings?.allow_guest || false,
          require_approval: groupData.settings?.require_approval || false,
          join_code: groupData.settings?.join_code || '',
          audio_quality: groupData.settings?.audio_quality || 2,
        },
      });
    } catch (err: any) {
      setError(err.message || 'Failed to load group');
    } finally {
      setLoading(false);
    }
  }, [groupId, getVoiceGroup]);

  useEffect(() => {
    loadGroup();
  }, [loadGroup]);

  const handleSave = async () => {
    if (!groupId) return;
    
    try {
      setSaving(true);
      // TODO: Add updateVoiceGroup method to the service
      toast({
        title: 'Update group API not implemented yet',
        status: 'warning',
        duration: 3000,
      });
      // await updateVoiceGroup(groupId, formData);
      // navigate(`/voice/groups/${groupId}`);
    } catch (err: any) {
      toast({
        title: 'Failed to update group settings',
        description: err.message,
        status: 'error',
        duration: 5000,
      });
    } finally {
      setSaving(false);
    }
  };

  const generateJoinCode = () => {
    const code = Math.random().toString(36).substring(2, 8).toUpperCase();
    setFormData(prev => ({
      ...prev,
      settings: { ...prev.settings, join_code: code }
    }));
  };

  // Check if user is group owner or admin
  // Debug logging to check IDs
  console.log('Owner ID from API:', group?.owner?.id);
  console.log('UserProfile ID:', profile?.id);
  console.log('Created by:', group?.created_by);
  
  const isOwner = group?.owner?.id === profile?.id;
  const isAdmin = group?.members?.members?.find(m => 
    m.userprofile_id === profile?.id && (m.role === 3 || m.role === 4)
  );

  // Update left sidebar
  useEffect(() => {
    const sidebarItems = [
      {
        label: 'All Rooms',
        path: '/voice/rooms',
      },
      {
        label: 'Group',
        path: `/voice/groups/${groupId}`,
      },
      {
        label: 'Settings',
        path: `/voice/groups/${groupId}/settings`,
        active: true,
      },
    ];

    window.updateLeftSidebar?.(sidebarItems);

    return () => window.updateLeftSidebar?.([]);
  }, [groupId]);

  if (loading) {
    return (
      <Box p={6}>
        <Skeleton height="40px" mb={6} />
        <Skeleton height="400px" />
      </Box>
    );
  }

  if (error) {
    return (
      <Box p={6}>
        <Alert status="error">
          <AlertIcon />
          {error}
        </Alert>
      </Box>
    );
  }

  if (!isOwner && !isAdmin) {
    return (
      <Box p={6}>
        <Alert status="warning">
          <AlertIcon />
          Only group owners and admins can modify settings
        </Alert>
        <Button mt={4} leftIcon={<FiArrowLeft />} onClick={() => navigate(`/voice/groups/${groupId}`)}>
          Back to Group
        </Button>
      </Box>
    );
  }

  return (
    <Box p={6}>
      <HStack justify="space-between" mb={6}>
        <Heading size="lg">Group Settings</Heading>
        <HStack>
          <Button variant="outline" leftIcon={<FiArrowLeft />} onClick={() => navigate(`/voice/groups/${groupId}`)}>
            Cancel
          </Button>
          <Button 
            colorScheme="blue" 
            leftIcon={<FiSave />} 
            onClick={handleSave}
            isLoading={saving}
            loadingText="Saving..."
          >
            Save Changes
          </Button>
        </HStack>
      </HStack>

      <Card>
        <CardHeader>
          <Heading size="md">Basic Information</Heading>
        </CardHeader>
        <CardBody>
          <VStack spacing={4} align="stretch">
            <FormControl isRequired>
              <FormLabel>Group Name</FormLabel>
              <Input
                value={formData.name}
                onChange={(e) => setFormData(prev => ({ ...prev, name: e.target.value }))}
                placeholder="Enter group name"
              />
            </FormControl>

            <FormControl>
              <FormLabel>Description</FormLabel>
              <Textarea
                value={formData.description}
                onChange={(e) => setFormData(prev => ({ ...prev, description: e.target.value }))}
                placeholder="Enter group description"
                rows={3}
              />
            </FormControl>

            <FormControl>
              <FormLabel>Group Type</FormLabel>
              <Select
                value={formData.type}
                onChange={(e) => setFormData(prev => ({ ...prev, type: parseInt(e.target.value) }))}
              >
                <option value={1}>Text</option>
                <option value={2}>Voice</option>
                <option value={3}>Video</option>
                <option value={4}>Streaming</option>
              </Select>
            </FormControl>
          </VStack>
        </CardBody>
      </Card>

      <Card mt={6}>
        <CardHeader>
          <Heading size="md">Group Settings</Heading>
        </CardHeader>
        <CardBody>
          <VStack spacing={4} align="stretch">
            <HStack>
              <FormControl>
                <FormLabel>Max Members</FormLabel>
                <NumberInput
                  value={formData.settings.max_members}
                  onChange={(_, value) => setFormData(prev => ({
                    ...prev,
                    settings: { ...prev.settings, max_members: value || 20 }
                  }))}
                  min={1}
                  max={100}
                >
                  <NumberInputField />
                  <NumberInputStepper>
                    <NumberIncrementStepper />
                    <NumberDecrementStepper />
                  </NumberInputStepper>
                </NumberInput>
              </FormControl>

              <FormControl>
                <FormLabel>Audio Quality</FormLabel>
                <Select
                  value={formData.settings.audio_quality}
                  onChange={(e) => setFormData(prev => ({
                    ...prev,
                    settings: { ...prev.settings, audio_quality: parseInt(e.target.value) }
                  }))}
                >
                  <option value={1}>Low</option>
                  <option value={2}>High</option>
                  <option value={3}>Ultra</option>
                </Select>
              </FormControl>
            </HStack>

            <FormControl display="flex" alignItems="center">
              <FormLabel htmlFor="is-public" mb="0">
                Public Group
              </FormLabel>
              <Switch
                id="is-public"
                isChecked={formData.settings.is_public}
                onChange={(e) => setFormData(prev => ({
                  ...prev,
                  settings: { ...prev.settings, is_public: e.target.checked }
                }))}
              />
            </FormControl>

            <FormControl display="flex" alignItems="center">
              <FormLabel htmlFor="allow-guest" mb="0">
                Allow Guests
              </FormLabel>
              <Switch
                id="allow-guest"
                isChecked={formData.settings.allow_guest}
                onChange={(e) => setFormData(prev => ({
                  ...prev,
                  settings: { ...prev.settings, allow_guest: e.target.checked }
                }))}
              />
            </FormControl>

            <FormControl display="flex" alignItems="center">
              <FormLabel htmlFor="require-approval" mb="0">
                Require Approval to Join
              </FormLabel>
              <Switch
                id="require-approval"
                isChecked={formData.settings.require_approval}
                onChange={(e) => setFormData(prev => ({
                  ...prev,
                  settings: { ...prev.settings, require_approval: e.target.checked }
                }))}
              />
            </FormControl>

            {!formData.settings.is_public && (
              <FormControl>
                <FormLabel>Join Code</FormLabel>
                <HStack>
                  <Input
                    value={formData.settings.join_code}
                    onChange={(e) => setFormData(prev => ({
                      ...prev,
                      settings: { ...prev.settings, join_code: e.target.value }
                    }))}
                    placeholder="Enter join code or generate one"
                  />
                  <Button onClick={generateJoinCode} size="sm">
                    Generate
                  </Button>
                </HStack>
                <Text fontSize="sm" color="gray.500" mt={1}>
                  Users will need this code to join the private group
                </Text>
              </FormControl>
            )}
          </VStack>
        </CardBody>
      </Card>
    </Box>
  );
};